import React, { useEffect, useState } from "react";
import { Modal } from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import dayjs from "dayjs";
import { useLocation } from "react-router";
import useApi from "../../../core/customhook/useApi";
import webservice_url, {
  apibaseurl,
} from "../../../core/webservice/Webservice_url";
import { PaginationType } from "../../../types/common-type";
import Datatable from "../../../core/common/dataTable";
import { all_routes } from "../../router/all_routes";
import { PageBreadcrumb } from "../../../core/common/BreadCrumbForm";
import Loading from "../../../core/common/custom-loading/Loading";
import Footer from "../../../core/common/Footer";
import { formattedDate } from "../../../core/methods/formattedDate";
import { cellWidth } from "../../../core/methods/tableCellWidth";
import PdfIcon from "../../../style/icon/svg/PdfIcon";
import { parseDate } from "../../../core/methods/parseDate";
import { safeLocaleCompare } from "../../../core/methods/sorterMethods";

interface DocumentItem {
  document_id: string;
  file_name: string;
  original_name: string;
  file_path: string;
  mime_type: string;
  uploaded_at: string;
}

interface Artifact {
  case_artifact_id: string;
  assignment_template_id: string;
  assignment_id: string;
  case_id: number;
  remarks: string;
  followup_date: string;
  created_by: string;
  created_at: string;
  documents: DocumentItem[];
}

const breadcrumbItem = [
  { label: "Case Details", path: all_routes.caseDetails },
  { label: "Add Artifacts" },
];

// DetailItem
interface DetailItemProps {
  label: string;
  value: string;
  placeholder?: string;
}

const DetailItem: React.FC<DetailItemProps> = ({
  label,
  value = "",
  placeholder = "",
}) => {
  const isEmpty = !value || value.trim() === "";

  return (
    <div className="col-md-4 mb-3">
      <label className="form-label fw-semibold" title={label}>
        {label}
      </label>
      <input
        type="text"
        className="form-control"
        value={isEmpty ? "" : value}
        placeholder={isEmpty ? placeholder || "—" : ""}
        disabled
        title={value}
      />
    </div>
  );
};

const AddArtifacts: React.FC = () => {
  const {
    case_id: caseId,
    assignment_id: assignId,
    assignment_template_id: assingTempId,
  } = useLocation()?.state || {};

  const [formData, setFormData] = useState<any>({});

  const [docsInfo, setDocsInfo] = useState<any[]>([]);

  const [allItemList, setAllItemList] = useState<Artifact[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<DocumentItem[]>(
    [],
  );
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const [pagination, setPagination] = useState<PaginationType>({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const getCaseDetailsAPI = useApi("", {
    autoCall: false,
    method: "GET",
    handleGlobalError: false,
  });
  const getArtifactsAPI = useApi("", {
    autoCall: false,
    method: "GET",
    handleGlobalError: false,
  });
  const getTemplateDocument = useApi("", {
    autoCall: false,
    method: "GET",
    handleGlobalError: false,
  });

  // ========== fetch Check data ===========
  const fetchCheckData = async () => {
    try {
      const res = await getCaseDetailsAPI.callApi(
        undefined,
        `${webservice_url.get_insufficiency_details}/${assingTempId}`,
      );
      if (res?.success) {
        const data = res?.data || {};
        setFormData(data);
      }
    } catch (err: any) {
      console.error(err?.response);
    }
  };

  useEffect(() => {
    if (!assingTempId) return;
    fetchCheckData();
  }, [assingTempId]);

  useEffect(() => {
    if (!assingTempId) return;
    fetchData();
  }, [pagination.current, pagination.pageSize, assingTempId, caseId]);

  const fetchData = async () => {
    try {
      const res = await getArtifactsAPI.callApi(
        undefined,
        `${webservice_url.get_all_artifacts}/${assingTempId}`,
      );

      if (res?.success) {
        const data: Artifact[] = res?.data || [];
        setAllItemList(data);

        setPagination((prev) => ({
          ...prev,
          total: res?.pagination?.totalRecords ?? data.length,
        }));
      }
    } catch (err: any) {
      console.error(err?.response);
    }
  };

  const handleViewDocuments = (documents: DocumentItem[]) => {
    setSelectedDocuments(documents || []);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedDocuments([]);
  };

  const handleTableChange = (paginationConfig: TablePaginationConfig) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationConfig.current || 1,
      pageSize: paginationConfig.pageSize || 10,
    }));
  };

  const getTemplateDocs = async () => {
    try {
      const res = await getTemplateDocument.callApi(
        undefined,
        `/api/v1/insufficiency/documents/${assingTempId}`,
      );

      if (res?.success) {
        setDocsInfo(res.data || []);
      }
    } catch (err) {
      console.error(err);
    }
  };
  useEffect(() => {
    if (!assingTempId) return;
    getTemplateDocs();
  }, [assingTempId]);

  const columns: ColumnsType<Artifact> = [
    {
      title: "Case ID",
      dataIndex: "case_id",
      width: 120,
      sorter: (a, b) => safeLocaleCompare(a, b, "case_id"),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      onCell: () => cellWidth(400),
      render: (remarks: string) =>
        remarks ? remarks : <span className="text-muted">-</span>,
      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "remarks"),
    },
    {
      title: "Follow-up Date",
      dataIndex: "followup_date",
      render: (date: string) => formattedDate(date),
      sorter: (a: any, b: any) =>
        parseDate(a.followup_date).getTime() -
        parseDate(b.followup_date).getTime(),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      render: (date: string) => formattedDate(date),
      sorter: (a: any, b: any) =>
        parseDate(a.created_at).getTime() - parseDate(b.created_at).getTime(),
    },
    {
      title: "Documents",
      width: 160,
      render: (_: unknown, record: Artifact) => {
        const count = record.documents?.length || 0;
        if (count === 0) {
          return <span className="text-muted">No documents</span>;
        }
        return (
          <button
            className="btn btn-sm btn-link p-0"
            onClick={() => handleViewDocuments(record.documents)}
          >
            View ({count})
          </button>
        );
      },
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content px-2 px-md-4 pb-5">
        <PageBreadcrumb
          title="Add Artifacts"
          breadcrumbs={breadcrumbItem}
          showBackButton={true}
          backRoute={`${all_routes.liveCaseProgress}`}
        />

        {getCaseDetailsAPI.loading ? (
          <Loading />
        ) : (
          <>
            <div className="card mt-3">
              <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h4 className="fw-bold mb-0">Artifacts History</h4>

                {!getArtifactsAPI.loading && (
                  <span className="badge bg-light text-dark border px-3 py-2">
                    {pagination.total}{" "}
                    {pagination.total === 1 ? "record" : "records"}
                  </span>
                )}
              </div>

              {/* table */}
              <div className="card-body">
                {getArtifactsAPI.loading ? (
                  <div style={{ padding: "40px 0" }}>
                    <Loading />
                  </div>
                ) : (
                  <Datatable
                    rowKey="case_artifact_id"
                    columns={columns}
                    dataSource={allItemList}
                    hideSizeChanger={true}
                    pagination={{
                      current: pagination.current,
                      pageSize: pagination.pageSize,
                      total: pagination.total,
                    }}
                    handleTableChange={handleTableChange}
                  />
                )}
              </div>
            </div>

            {/* case details */}
            <div className="card mt-3">
              <div className="card-header bg-white py-3 border-bottom">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div className="d-flex align-items-center">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle bg-warning-subtle me-3"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="ti ti-file-text text-warning fs-3"></i>
                    </div>

                    <div>
                      <h6 className="fw-bold mb-0">{formData?.check_name}</h6>
                      <small className="text-muted">
                        Last Updated:{" "}
                        {dayjs(formData?.updated_at).format(
                          "DD MMM YYYY, hh:mm A",
                        )}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-body">
                <div className="row gx-3">
                  <DetailItem
                    label="Form Type"
                    value={formData?.form_type
                      ?.replaceAll("_", " ")
                      .toUpperCase()}
                  />
                  {formData?.fields?.length > 0 &&
                    formData?.fields
                      ?.filter((f: any) =>
                        ["text", "number", "dropdown"]?.includes(f?.field_type),
                      )
                      ?.map((d: any) => (
                        <DetailItem
                          key={d?.field_id}
                          label={d?.label}
                          value={d?.value}
                          placeholder={d?.placeholder}
                        />
                      ))}
                </div>
              </div>
            </div>

            {/* case documents */}
            <div className="card mt-3">
              <div className="card-header bg-white py-3 border-bottom">
                <div className="d-flex align-items-center">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle bg-warning-subtle me-3"
                    style={{ width: 40, height: 40 }}
                  >
                    <i className="ti ti-file-text text-warning fs-3"></i>
                  </div>

                  <div>
                    <h6 className="fw-bold mb-0">Uploaded Documents</h6>
                    <small className="text-muted">
                      Candidate uploaded supporting documents
                    </small>
                  </div>
                </div>
              </div>
              {getTemplateDocument.loading ? (
                <Loading />
              ) : (
                <div className="card-body">
                  {docsInfo?.length > 0 ? (
                    <div className="row g-3">
                      {docsInfo.map((item) => (
                        <div
                          className="col-xl-3 col-lg-4 col-md-6"
                          key={item.file_id}
                        >
                          <div
                            className="card h-100 border-0 shadow-sm"
                            style={{
                              borderRadius: "14px",
                              transition: "0.25s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform =
                                "translateY(-4px)";
                              e.currentTarget.style.boxShadow =
                                "0 8px 20px rgba(0,0,0,0.12)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "translateY(0)";
                              e.currentTarget.style.boxShadow = "";
                            }}
                          >
                            <div className="card-body text-center">
                              {/* Icon */}
                              <div
                                className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                                style={{
                                  width: 70,
                                  height: 70,
                                  borderRadius: "50%",
                                  background: "#FDECEC",
                                }}
                              >
                                <PdfIcon size={42} color="#D32F2F" />
                              </div>

                              {/* Document Name */}
                              <h6
                                className="fw-bold text-capitalize mb-2"
                                title={item.field_key.replaceAll("_", " ")}
                              >
                                {item.field_key.replaceAll("_", " ")}
                              </h6>

                              {/* File Name */}
                              <div className="small text-muted text-truncate mb-3">
                                {item.original_name}
                              </div>

                              {/* Info */}
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="badge bg-info-subtle text-info-emphasis border border-info-subtle">
                                  {item.mime_type.split("/")[1]?.toUpperCase()}
                                </span>

                                <span className="badge bg-light text-dark border">
                                  {new Date(
                                    item.uploaded_at,
                                  ).toLocaleDateString()}
                                </span>
                              </div>

                              {/* View Button */}
                              <a
                                href={`${apibaseurl}/uploads/${item.s3_key}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-primary w-100 mt-2"
                              >
                                <i className="ti ti-eye me-1"></i>
                                View Document
                              </a>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <i
                        className="ti ti-folder-off text-secondary"
                        style={{ fontSize: "48px" }}
                      ></i>

                      <h6 className="mt-3 mb-1">No Documents Found</h6>

                      <p className="text-muted mb-0">
                        No uploaded documents are available for this candidate.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* view documents modal */}
        <Modal
          title={
            <span className="fw-bold">
              <i className="ti ti-folder-open me-2 text-primary"></i>
              Documents
            </span>
          }
          open={modalOpen}
          onCancel={handleModalClose}
          footer={null}
          width={700}
        >
          {selectedDocuments.length === 0 ? (
            <div className="text-center py-5">
              <i
                className="ti ti-folder-off text-secondary"
                style={{ fontSize: "48px" }}
              ></i>

              <h6 className="mt-3 mb-1">No Documents Found</h6>

              <p className="text-muted mb-0">No documents available</p>
            </div>
          ) : (
            <div className="row g-3">
              {selectedDocuments.map((doc) => (
                <div className="col-md-6" key={doc.document_id}>
                  <div
                    className="card h-100 border-0 shadow-sm"
                    style={{
                      borderRadius: "14px",
                      transition: "0.25s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow =
                        "0 8px 20px rgba(0,0,0,0.12)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "";
                    }}
                  >
                    <div className="card-body">
                      <div className="d-flex">
                        {/* Icon */}
                        <div
                          className="d-flex align-items-center justify-content-center rounded-circle bg-danger-subtle me-3"
                          style={{
                            width: 55,
                            height: 55,
                            flexShrink: 0,
                          }}
                        >
                          <i className="ti ti-file-text text-danger fs-3"></i>
                        </div>

                        {/* Details */}
                        <div className="flex-grow-1">
                          <h6
                            className="fw-bold mb-1 text-truncate"
                            title={doc.original_name}
                          >
                            {doc.original_name}
                          </h6>

                          <div className="d-flex align-items-center gap-1 mb-1">
                            <span className="text-muted small">Type:</span>
                            <span className="badge bg-info-subtle text-info-emphasis border border-info-subtle">
                              {doc.mime_type}
                            </span>
                          </div>

                          <div className="text-muted small mb-2">
                            Uploaded: {formattedDate(doc.uploaded_at)}
                          </div>
                          <a
                            href={`${apibaseurl}/uploads/${doc.file_name}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-primary"
                          >
                            <i className="ti ti-eye me-1"></i>
                            View
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal>
      </div>
      <Footer />
    </div>
  );
};

export default AddArtifacts;
